// askSmart-ui/src/pages/TOSP/portal/SalesHistory.tsx
import React, { useState } from "react";
import { formatCurrency } from "../format";
import { Tenant, tenantLabel } from "../types";
import { MONTH_NAMES, monthName, SALES_DUE_DAY, YearMonth } from "./portalSummary";
import SalesEntryModal from "./SalesEntryModal";
import {
  bestYearForMonth,
  hasSubmitted,
  MonthMovement,
  monthOnMonth,
  reportedYears,
  TenantSalesMonth,
  yearOnYear,
} from "./tenantSales";

interface SalesHistoryProps {
  tenant: Tenant;
  sales: TenantSalesMonth[];
  onSubmitSales: (target: YearMonth, grossRevenue: number) => void;
  onBack: () => void;
}

/** The first year sales were reported through TOSP. */
const EARLIEST_YEAR = 2019;

type CellState = "submitted" | "due" | "notDue";

interface Cell {
  year: number;
  month: number;
  state: CellState;
  grossSales: number | null;
  dueOn: Date;
}

/** Wording for an arrow, from the shared month-on-month comparison. */
const movementLabel = (moved: MonthMovement, month: number, year: number): string => {
  const direction = moved.up ? "up" : "down";
  return moved.changePercent === null
    ? `${direction} on ${year - 1}`
    : `${moved.changePercent.toFixed(1)}% ${direction} on ${monthName(month)} ${year - 1}`;
};

// Sales by month, one column per year, so any set of years can be read side by
// side. Which years show is the tenant's choice — two to compare like for like,
// or every year at once to see a trend — rather than one at a time behind tabs,
// where a comparison means remembering the column you just left.
export default function SalesHistory({
  tenant,
  sales,
  onSubmitSales,
  onBack,
}: SalesHistoryProps) {
  const now = new Date();
  const thisYear = now.getFullYear();

  // Every year that can be reported, newest first, plus any older one that
  // already holds figures so imported history is never unreachable.
  const years = [
    ...new Set([
      ...Array.from({ length: thisYear - EARLIEST_YEAR + 1 }, (_, i) => thisYear - i),
      ...reportedYears(sales, tenant.id),
    ]),
  ].sort((a, b) => b - a);

  // Opens with every year on: the whole history at once is the view worth
  // landing on, and turning years off to narrow it is easier than hunting for
  // the ones to turn on.
  const [shown, setShown] = useState<number[]>(years);
  const [entryFor, setEntryFor] = useState<Cell | null>(null);

  // Newest on the left, however they were clicked on.
  const columns = [...shown].sort((a, b) => b - a);

  const toggle = (year: number) =>
    setShown((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
    );

  const cellFor = (year: number, month: number): Cell => {
    const entry = sales.find(
      (record) =>
        record.tenantId === tenant.id && record.year === year && record.month === month,
    );
    // A month's takings can't be totalled until it's over, so its window opens
    // on the 1st of the month after and it's due on the due day of that month.
    const opensOn = new Date(year, month, 1);
    return {
      year,
      month,
      state: entry ? "submitted" : now >= opensOn ? "due" : "notDue",
      grossSales: entry ? entry.grossSales : null,
      dueOn: new Date(year, month, SALES_DUE_DAY),
    };
  };

  // Marked whichever year holds it, on or off screen — same helper the admin
  // sheet uses, so "best ever" can't mean two different things.
  const best = bestYearForMonth(sales, tenant.id);

  const yearTotal = (year: number) =>
    sales
      .filter((entry) => entry.tenantId === tenant.id && entry.year === year)
      .reduce((sum, entry) => sum + entry.grossSales, 0);

  // Fixed widths, not fractions. A column that stretches to fill the space
  // moves every figure in it whenever another year is switched off — the
  // current year has to hold its place on the left through any change.
  const gridColumns = `104px repeat(${columns.length}, 132px)`;

  return (
    <div className="tosp-portal tosp-portal-wide">
      <header className="tosp-portal-detail-header">
        <button type="button" className="tosp-portal-back" onClick={onBack}>
          ‹ Dashboard
        </button>
        {/* The business is named on every detail screen: an account holding
            several tenancies looks identical on all of them otherwise, and an
            admin viewing one has nothing else to go on. */}
        <h1 className="tosp-portal-detail-title">
          Sales <span className="tosp-portal-detail-who">({tenantLabel(tenant)})</span>
        </h1>
      </header>

      <div className="tosp-portal-years" role="group" aria-label="Years shown">
        {years.map((year) => {
          const on = shown.includes(year);
          return (
            <button
              key={year}
              type="button"
              aria-pressed={on}
              className={`tosp-portal-year-tab${on ? " tosp-portal-year-tab-active" : ""}`}
              onClick={() => toggle(year)}
            >
              {year}
            </button>
          );
        })}
      </div>

      {columns.length === 0 ? (
        <div className="tosp-portal-notice">
          <strong>No years selected.</strong>
          <p>Choose a year above to see its monthly sales.</p>
        </div>
      ) : (
        // Scrolls sideways once there are more columns than fit; the month
        // names stay pinned so a figure is never orphaned from its row.
        <div className="tosp-portal-sheet-scroll">
          <div className="tosp-portal-sheet">
            <div className="tosp-portal-sheet-head" style={{ gridTemplateColumns: gridColumns }}>
              <div className="tosp-portal-sheet-label" />
              {columns.map((year) => (
                <div key={year} className="tosp-portal-sheet-year">
                  {year}
                </div>
              ))}
            </div>

            {MONTH_NAMES.map((_, index) => {
              const month = index + 1;
              return (
                <div
                  key={month}
                  className="tosp-portal-sheet-row"
                  style={{ gridTemplateColumns: gridColumns }}
                >
                  <div className="tosp-portal-sheet-label">{monthName(month)}</div>
                  {columns.map((year) => {
                    const cell = cellFor(year, month);
                    if (cell.state === "submitted") {
                      const moved = monthOnMonth(sales, tenant.id, year, month);
                      const label = moved ? movementLabel(moved, month, year) : undefined;
                      const isBest = best.get(month) === year;
                      return (
                        <div
                          key={year}
                          className={`tosp-portal-sheet-cell${isBest ? " tosp-portal-sheet-best" : ""}`}
                          title={
                            isBest ? `Best ${monthName(month)} on record` : undefined
                          }
                        >
                          <span>{formatCurrency(cell.grossSales as number)}</span>
                          {/* Always rendered, empty when there's nothing to
                              compare — the figures stay in one column instead
                              of jogging left wherever an arrow appears. */}
                          <span
                            className={`tosp-portal-arrow${
                              moved ? (moved.up ? " tosp-portal-arrow-up" : " tosp-portal-arrow-down") : ""
                            }`}
                            title={label}
                            aria-label={label}
                          >
                            {moved ? (moved.up ? "▲" : "▼") : ""}
                          </span>
                        </div>
                      );
                    }
                    if (cell.state === "due") {
                      return (
                        <div key={year} className="tosp-portal-sheet-cell">
                          <button
                            type="button"
                            className="tosp-portal-add-cell"
                            title={`Report ${monthName(month)} ${year}`}
                            onClick={() => setEntryFor(cell)}
                          >
                            + Add
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={year}
                        className="tosp-portal-sheet-cell tosp-portal-sheet-empty"
                        title="Not yet due"
                      >
                        —
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div
              className="tosp-portal-sheet-row tosp-portal-sheet-total"
              style={{ gridTemplateColumns: gridColumns }}
            >
              <div className="tosp-portal-sheet-label">Total</div>
              {columns.map((year) => {
                // Like for like, which matters most here: 2026's seven months
                // against a full 2025 would read as a 37% collapse. `yearOnYear`
                // compares only the months this year actually holds.
                const change = yearOnYear(sales, tenant.id, year);
                const up = (change?.changePercent ?? 0) > 0;
                const moved =
                  change && change.changePercent !== null && change.changePercent !== 0;
                const label = !change
                  ? undefined
                  : change.monthsCompared < 12
                    ? `${Math.abs(change.changePercent ?? 0).toFixed(1)}% ${up ? "up" : "down"} on the same ${change.monthsCompared} months of ${year - 1}`
                    : `${Math.abs(change.changePercent ?? 0).toFixed(1)}% ${up ? "up" : "down"} on ${year - 1}`;

                return (
                  <div key={year} className="tosp-portal-sheet-cell">
                    <span>{formatCurrency(yearTotal(year))}</span>
                    <span
                      className={`tosp-portal-arrow${
                        moved ? (up ? " tosp-portal-arrow-up" : " tosp-portal-arrow-down") : ""
                      }`}
                      title={moved ? label : undefined}
                      aria-label={moved ? label : undefined}
                    >
                      {moved ? (up ? "▲" : "▼") : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {entryFor && (
        <SalesEntryModal
          target={{ year: entryFor.year, month: entryFor.month }}
          dueOn={entryFor.dueOn}
          onClose={() => setEntryFor(null)}
          onSubmit={(grossRevenue) => {
            // Guarded here as well as in the store: this sheet offers a way in
            // for every month of every year on screen.
            if (!hasSubmitted(sales, tenant.id, entryFor.year, entryFor.month)) {
              onSubmitSales({ year: entryFor.year, month: entryFor.month }, grossRevenue);
            }
            setEntryFor(null);
          }}
        />
      )}
    </div>
  );
}
